/**
 * @file Данные задачи загрузки и расчёты без привязки к экрану.
 *
 * Request содержит файл и секреты; State — только данные для интерфейса.
 * Сегмент — одна параллельная часть файла; чанк — блок 512 КиБ внутри сегмента.
 */
export const CHUNK_SIZE = 512 * 1024
export const MAX_SEGMENTS = 4

export const uploadErrorCodes = [
    'quota',
    'quotaCheck',
    'network',
    'rateLimited',
    'unauthorized',
    'conflict',
    'invalidFile',
    'protocol',
    'offsetMismatch',
    'worker',
    'encryption',
    'finalizationFailed',
    'finalizationUnknown',
] as const

export type UploadErrorCode = (typeof uploadErrorCodes)[number]

export function isUploadErrorCode(value: unknown): value is UploadErrorCode {
    return (
        typeof value === 'string' &&
        uploadErrorCodes.includes(value as UploadErrorCode)
    )
}

export class UploadError extends Error {
    readonly code: UploadErrorCode
    readonly keepParts: boolean

    constructor(code: UploadErrorCode, keepParts = false) {
        super(code)
        this.name = 'UploadError'
        this.code = code
        this.keepParts = keepParts
    }
}

export function asUploadError(error: unknown): UploadError {
    return error instanceof UploadError ? error : new UploadError('network')
}

export interface SegmentRange {
    index: number
    start: number
    end: number
}

export type SegmentStatus =
    | 'waiting'
    | 'uploading'
    | 'retrying'
    | 'done'
    | 'stopped'
    | 'failed'

export interface SegmentProgress extends SegmentRange {
    uploaded: number
    status: SegmentStatus
}

export type UploadPhase =
    | 'checking'
    | 'uploading'
    | 'finalizing'
    | 'cleaning'
    | 'cancelling'
    | 'succeeded'
    | 'failed'
    | 'cancelled'

// Публичное состояние для UI; файл, пароль и токен остаются в отдельном Request.
export interface UploadState {
    id: string
    name: string
    size: number
    boxId: string
    folder: string
    phase: UploadPhase
    segments: SegmentProgress[]
    error: UploadErrorCode | null
    cleanupWarning: boolean
    retryable: boolean
}

export interface UploadRequest {
    file: File
    boxId: string
    folder: string
    token: string
    password: string
}

export function isUploadBusy(state: UploadState | null): boolean {
    return (
        state !== null &&
        !['succeeded', 'failed', 'cancelled'].includes(state.phase)
    )
}

/** Распределяет файл по максимум четырём сегментам, выровненным по чанкам. */
export function splitFile(size: number): SegmentRange[] {
    if (!Number.isSafeInteger(size) || size < 0)
        throw new UploadError('invalidFile')

    // Число чанков определяет число параллельных сегментов: примерно один
    // сегмент на четыре чанка, но не больше MAX_SEGMENTS. Даже пустому файлу
    // нужен один handle для последующей серверной сборки.
    const chunks = Math.max(1, Math.ceil(size / CHUNK_SIZE))
    const count = Math.min(MAX_SEGMENTS, Math.max(1, Math.ceil(chunks / 4)))

    // Округляем размер сегмента до целого чанка. Последний диапазон обрезается
    // по size; end не включён. Пример 5 МиБ разобран в соседнем README.
    const segmentSize = Math.ceil(chunks / count) * CHUNK_SIZE

    return Array.from({ length: count }, (_, index) => ({
        index,
        start: Math.min(index * segmentSize, size),
        end: Math.min((index + 1) * segmentSize, size),
    }))
}

// AES-CBC добавляет к чанку соль 16 байт и padding от 1 до 16 байт.
// Заголовки и одновременные операции могут потребовать дополнительный трафик.
export function minimumTraffic(size: number, encrypted: boolean): number {
    if (!encrypted || size === 0) return size

    const full = Math.floor(size / CHUNK_SIZE)
    const tail = size % CHUNK_SIZE

    return (
        full * (CHUNK_SIZE + 32) + (tail ? tail + 16 + (16 - (tail % 16)) : 0)
    )
}

export function totalUploaded(state: UploadState): number {
    return state.segments.reduce(
        (total, segment) => total + segment.uploaded,
        0,
    )
}

export function uploadPercent(state: UploadState): number {
    // Переданные байты ещё не подтверждают успешную серверную сборку.
    if (state.phase === 'succeeded') return 100

    return state.size === 0
        ? 0
        : Math.min(99, Math.floor((totalUploaded(state) / state.size) * 100))
}

export function checkUploadResponse(response: Response): void {
    if (response.ok) return

    if (response.status === 401 || response.status === 403)
        throw new UploadError('unauthorized')

    if (response.status === 409) throw new UploadError('conflict')

    if (response.status === 429) throw new UploadError('rateLimited')

    if ([400, 404, 410, 413, 415, 422].includes(response.status))
        throw new UploadError('protocol')

    throw new UploadError('network')
}
